/* eslint-disable sonarjs/no-duplicate-string */
import { useI18n } from "@core/i18n/i18n";
import { libraryFilterManager, onboardingManager } from "@core/service-container";
import { Patient, PendingPatient } from "@domain/patient/patient";
import {
	ProgramContent,
	programContentAreEquals,
	programContentDeepCopy,
	updateExerciseFromProgram,
} from "@domain/program/program";
import { ProgramContentEditor } from "@domain/program/program-content-editor";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { MenuItemProps } from "@ui/components/common/menu-button";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { useQuitBlocking } from "@ui/navigation/use-quit-blocking";
import { DayTable } from "@ui/pages/program-builder/day-table/day-table";
import { ProgramBuilderDragDropContextProvider } from "@ui/pages/program-builder/drag-drop/program-builder-drag-drop-context";
import { HeaderEditorButtonProps, ProgramBuilderHeader } from "@ui/pages/program-builder/header/program-builder-header";
import { ExerciseLibrary } from "@ui/pages/program-builder/library/sidebar/exercise-library";
import { LibrarySidebar } from "@ui/pages/program-builder/library/sidebar/library-sidebar";
import { MobileDayCarousel } from "@ui/pages/program-builder/mobile/mobile-day-carousel";
import { MobileProgramContentFooter } from "@ui/pages/program-builder/mobile/mobile-program-content-footer";
import { useExerciseCardOnboardingAnalytics } from "@ui/pages/program-builder/onboarding/hooks/use-exercise-card-onboarding-analytics";
import { OnboardingContextProvider } from "@ui/pages/program-builder/onboarding/onboarding-context-provider";
import { QuickstartPopover } from "@ui/pages/program-builder/quickstart/quickstart-popover";
import { showComplementaryTooltip } from "@ui/pages/program-builder/quickstart/complementary-tooltip-signal";
import { QuickstartStepDefinition, QuickstartStepState } from "@ui/pages/program-builder/quickstart/quickstart-types";
import { showAlternativeHalo, resetAlternativeGuide3 } from "@ui/pages/program-builder/quickstart/alternative-highlight-signal";
import { showDuplicateSessionHalo } from "@ui/pages/program-builder/quickstart/duplicate-session-signal";
import { requestStartGuide02 } from "@ui/pages/program-builder/quickstart/start-guide-signal";
import { isNotNil } from "@utils/assert";
import { OnboardingProgramQuickstartStep, OnboardingStepStatus } from "@domain/onboarding/onboarding";
import { useObservable } from "micro-observables";
import React, { useEffect, useState } from "react";
import { QuickstartGuideName, trackCompleteQuickstart } from "@ui/pages/program-builder/quickstart/quickstart-analytics";
import { hasGuideCompletionTracked, markGuideCompletionTracked } from "@ui/pages/program-builder/quickstart/quickstart-completion-storage";

type EditorButtonProps = Omit<HeaderEditorButtonProps, "onClick" | "tooltip"> & {
	onClick?: (programContent: ProgramContent) => void;
	disabledTooltipMessage?: string;
};

export interface ProgramContentEditorViewProps {
	patient?: Patient | PendingPatient;
	programContent: ProgramContent;
	programEditor: ProgramContentEditor;
	programStatus?: ProgramStatus;
	statusMessage: string;
	availableActions: MenuItemProps[];
	secondaryButton?: EditorButtonProps;
	primaryButton?: EditorButtonProps;
	dataIsLoading: boolean;
	quitBlockingEnabled?: boolean;
	viewOnly?: boolean;
	disabledLibraryTooltip?: string;
	initialNotSavedProgramContent?: ProgramContent;
	onProgramContentChanged?: (programContent: ProgramContent) => void;
	onProgramSchedulerClick?: (programContent: ProgramContent) => void;
}

export const ProgramContentEditorView: React.FC<ProgramContentEditorViewProps> = ({
	patient,
	programContent,
	programEditor,
	programStatus,
	statusMessage,
	availableActions,
	secondaryButton,
	primaryButton,
	dataIsLoading,
	quitBlockingEnabled = true,
	viewOnly = false,
	disabledLibraryTooltip,
	initialNotSavedProgramContent,
	onProgramContentChanged,
	onProgramSchedulerClick,
}) => {
	const { format } = useI18n();
		const { isDesktop } = useDimensions();

		const editedProgram = useObservable(programEditor.editedProgram);
	const exerciseWithAlternativesEdited = useObservable(libraryFilterManager.exerciseWithAlternativesEdited);
	const onboardingStepsStatus = useObservable(onboardingManager.onboardingStepsStatus);

	const editedProgramWithUpdatedAlternativeExercises = updateExerciseFromProgram(
		editedProgram,
		exerciseWithAlternativesEdited?.draggableId,
		exerciseWithAlternativesEdited,
	);

	const hasPendingChanges = !programContentAreEquals(editedProgramWithUpdatedAlternativeExercises, programContent);
	useQuitBlocking(quitBlockingEnabled && hasPendingChanges);

	useEffect(() => {
		programEditor.setDataIsLoading(dataIsLoading);
	}, [dataIsLoading, programEditor]);

	useEffect(() => {
		if (initialNotSavedProgramContent) {
			programEditor.editProgram(programContentDeepCopy(initialNotSavedProgramContent));
		}
	}, [initialNotSavedProgramContent, programEditor]);

	useEffect(() => {
		onProgramContentChanged?.(editedProgramWithUpdatedAlternativeExercises);
	}, [editedProgram, onProgramContentChanged, editedProgramWithUpdatedAlternativeExercises]);

	useExerciseCardOnboardingAnalytics(programStatus);


	const [showAddExerciseGuidance, setShowAddExerciseGuidance] = useState(false);
	const [showFilterLibraryGuidance, setShowFilterLibraryGuidance] = useState(false);
	const [primaryQuickstartTooltipVisible, setPrimaryQuickstartTooltipVisible] = useState(false);
	const firstExerciseDraggableId = useObservable(programEditor.firstExerciseDraggableId);
	const hasAtLeastOneExercise = isNotNil(firstExerciseDraggableId);

	const isQuickstartStepCompleted = (step: OnboardingProgramQuickstartStep | undefined) => {
		if (!step) return false;
		const status = onboardingStepsStatus ? onboardingStepsStatus[step] : undefined;
		return (
			status === OnboardingStepStatus.DISMISSED || status === OnboardingStepStatus.DISMISSED_BUT_DISPLAYED
		);
	};

	const addExerciseCompleted = isQuickstartStepCompleted(
		OnboardingProgramQuickstartStep.ADD_EXERCISE_GUIDE_01,
	);
	const filterGuide01Completed = isQuickstartStepCompleted(
		OnboardingProgramQuickstartStep.FILTER_LIBRARY_GUIDE_01,
	);
	const filterGuide02Completed = isQuickstartStepCompleted(
		OnboardingProgramQuickstartStep.FILTER_LIBRARY_GUIDE_02,
	);
	const complementaryGuide01Completed = isQuickstartStepCompleted(
		OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_01,
	);
	const complementaryGuide02Completed = isQuickstartStepCompleted(
		OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_02,
	);
	const complementaryGuide03Completed = isQuickstartStepCompleted(
			OnboardingProgramQuickstartStep.COMPLEMENTARY_EXERCISES_GUIDE_03,
	);
	const addComplementaryCompleted =
		complementaryGuide01Completed && complementaryGuide02Completed && complementaryGuide03Completed;

	const duplicateGuide01Completed = isQuickstartStepCompleted(
		OnboardingProgramQuickstartStep.DUPLICATE_SESSION_GUIDE_01,
	);
	const duplicateGuide02Completed = isQuickstartStepCompleted(
		OnboardingProgramQuickstartStep.DUPLICATE_SESSION_GUIDE_02,
	);

	const startGuide02Completed = isQuickstartStepCompleted(
		OnboardingProgramQuickstartStep.START_GUIDE_02,
	);
	const startProgramCompleted = startGuide02Completed;

	const quickstartStates = {
		Locked: "locked" as QuickstartStepState,
		Active: "active" as QuickstartStepState,
		Completed: "completed" as QuickstartStepState,
		CompletedDisabled: "completed-disabled" as QuickstartStepState,
	} as const;

	const computeAddComplementaryState = (): QuickstartStepState => {
		if (addComplementaryCompleted) return quickstartStates.Completed;
		if (hasAtLeastOneExercise) return quickstartStates.Active;
		return quickstartStates.Locked;
	};

	const alternativeGuideStepsCompleted = [
		OnboardingProgramQuickstartStep.ALTERNATIVE_EXERCISES_GUIDE_01,
		OnboardingProgramQuickstartStep.ALTERNATIVE_EXERCISES_GUIDE_02,
		OnboardingProgramQuickstartStep.ALTERNATIVE_EXERCISES_GUIDE_03,
	].every(
		step => isQuickstartStepCompleted(step)
	);

	const computeDuplicateSessionState = (): QuickstartStepState => {
		if (duplicateGuide01Completed && duplicateGuide02Completed) return quickstartStates.Completed;
		if (hasAtLeastOneExercise) return quickstartStates.Active;
		return quickstartStates.Locked;
	};
	
	useEffect(() => {
		const trackOnce = (guide: QuickstartGuideName, completed: boolean) => {
			if (!completed) return;
			if (hasGuideCompletionTracked(guide)) return;
			trackCompleteQuickstart(guide);
			markGuideCompletionTracked(guide);
		};

		trackOnce("ADD_FIRST_EXERCISE", addExerciseCompleted);
		trackOnce("FILTER_LIBRARY", filterGuide01Completed && filterGuide02Completed);
		trackOnce("ADD_COMPLEMENTARY_EXERCISES", addComplementaryCompleted);
		trackOnce("ADD_ALTERNATIVE_EXERCISES", alternativeGuideStepsCompleted);
		trackOnce("DUPLICATE_SESSION", duplicateGuide01Completed && duplicateGuide02Completed);
		trackOnce("START_SESSION", startProgramCompleted);
	}, [
		addExerciseCompleted,
		filterGuide01Completed,
		filterGuide02Completed,
		addComplementaryCompleted,
		alternativeGuideStepsCompleted,
		duplicateGuide01Completed,
		duplicateGuide02Completed,
		startProgramCompleted,
	]);

	const quickstartSteps: QuickstartStepDefinition[] = [
				 {
						 id: "add-exercise",
						 title: format("programBuilder.quickstart.step.addFirstExercise"),
						 computeState: () => (addExerciseCompleted ? quickstartStates.Completed : quickstartStates.Active),
						 onClick: () => setShowAddExerciseGuidance(true),
				 },
				 {
						 id: "filter-library",
						 title: format("programBuilder.quickstart.step.filterLibrary"),
						 computeState: () => (filterGuide01Completed && filterGuide02Completed ? quickstartStates.Completed : quickstartStates.Active),
						 onClick: () => setShowFilterLibraryGuidance(true),
				 },
				 {
						 id: "add-complementary",
						 title: format("programBuilder.quickstart.step.addComplementaryExercises"),
						 computeState: () => {
							if (!hasAtLeastOneExercise) {
								return addComplementaryCompleted ? quickstartStates.CompletedDisabled : quickstartStates.Locked;
							}
							return computeAddComplementaryState();
						},
						 onClick: () => showComplementaryTooltip(),
				 },
				 {
						 id: "add-alternatives",
						 title: format("programBuilder.quickstart.step.addAlternativeExercises"),
						 computeState: () => {
							if (!hasAtLeastOneExercise) {
								return alternativeGuideStepsCompleted ? quickstartStates.CompletedDisabled : quickstartStates.Locked;
							}
							return alternativeGuideStepsCompleted ? quickstartStates.Completed : quickstartStates.Active;
						},
					 onClick: () => { resetAlternativeGuide3(); showAlternativeHalo(); },
				 },

		{
			id: "duplicate-session",
			title: format("programBuilder.quickstart.step.duplicateSession"),
			computeState: () => {
				if (!hasAtLeastOneExercise) {
					return (duplicateGuide01Completed && duplicateGuide02Completed) ? quickstartStates.CompletedDisabled : quickstartStates.Locked;
				}
				return computeDuplicateSessionState();
			},
			onClick: () => showDuplicateSessionHalo(),
		},
		{
			id: "start-program",
			title: format("programBuilder.quickstart.step.startProgram"),
			computeState: () => {
				if (!hasAtLeastOneExercise) {
					return startProgramCompleted ? quickstartStates.CompletedDisabled : quickstartStates.Locked;
				}
				if (patient && programStatus && programStatus !== ProgramStatus.Draft) {
					return startProgramCompleted ? quickstartStates.CompletedDisabled : quickstartStates.Locked;
				}
				if (startProgramCompleted) return quickstartStates.Completed;
				return quickstartStates.Active;
			},
			onClick: () => {
				if (!patient || programStatus === ProgramStatus.Draft) {
					setPrimaryQuickstartTooltipVisible(true);
					requestStartGuide02();
				}
			},
		},
	];

	return (
		<OnboardingContextProvider programEditor={programEditor} programStatus={programStatus}>
			<ProgramBuilderDragDropContextProvider programEditor={programEditor}>
					<ProgramBuilderHeader
					patient={patient}
					programContent={editedProgram}
					programStatus={programStatus}
					statusMessage={statusMessage}
					programActions={availableActions}
						secondaryButton={getButtonProps(secondaryButton, editedProgramWithUpdatedAlternativeExercises)}
						primaryButton={getButtonProps(
							primaryButton && {
								...primaryButton,
								onClick: async (pc: ProgramContent) => {
									setPrimaryQuickstartTooltipVisible(false);
									const startStep = patient
										? OnboardingProgramQuickstartStep.START_DRAFT_GUIDE_01
										: OnboardingProgramQuickstartStep.START_TEMPLATE_GUIDE_01;
									await onboardingManager.passStepOnboarding(startStep);
									primaryButton.onClick?.(pc);
								},
							},
							editedProgramWithUpdatedAlternativeExercises,
						)}
						primaryQuickstartTooltipStep={
							patient ? OnboardingProgramQuickstartStep.START_DRAFT_GUIDE_01 : OnboardingProgramQuickstartStep.START_TEMPLATE_GUIDE_01
						}
						primaryQuickstartTooltipVisible={primaryQuickstartTooltipVisible}
						onPrimaryQuickstartTooltipRequestClose={async () => {
							setPrimaryQuickstartTooltipVisible(false);
							const startStep = patient
								? OnboardingProgramQuickstartStep.START_DRAFT_GUIDE_01
								: OnboardingProgramQuickstartStep.START_TEMPLATE_GUIDE_01;
							await onboardingManager.passStepOnboarding(startStep);
						}}
					onNameChanged={newName => programEditor.renameProgram(newName)}
					onProgramSchedulerClick={() => onProgramSchedulerClick?.(editedProgram)}
					editable={!viewOnly}
				/>
				{isDesktop ? (
					<LibrarySidebar
						disabled={viewOnly}
						disabledTooltip={disabledLibraryTooltip}
						pushedContent={
							<StyledDayTable disabled={viewOnly} editedProgram={editedProgram} programEditor={programEditor} />
						}
						title={format("exerciseLibrary.header")}
					>
						<ExerciseLibrary
							areAlternativesEdited={isNotNil(libraryFilterManager.exerciseWithAlternativesEdited.get())}
							showAddExerciseGuidance={showAddExerciseGuidance}
							onHideAddExerciseGuidance={() => setShowAddExerciseGuidance(false)}
							showFilterLibraryGuidance={showFilterLibraryGuidance}
							onHideFilterLibraryGuidance={() => setShowFilterLibraryGuidance(false)}
						/>
					</LibrarySidebar>
				) : (
					<>
						<StyledMobileDayCarousel disabled={viewOnly} programEditor={programEditor} />
						<MobileProgramContentFooter
							primaryButton={getButtonProps(primaryButton, editedProgramWithUpdatedAlternativeExercises)}
							secondaryButton={getButtonProps(secondaryButton, editedProgramWithUpdatedAlternativeExercises)}
						/>
					</>
				)}
				{/* Quickstart visible only on desktop for now */}
				{isDesktop && <QuickstartPopover steps={quickstartSteps} />}
			</ProgramBuilderDragDropContextProvider>
		</OnboardingContextProvider>
	);
};

const disableElementCSS = ({ disabled }: { disabled: boolean }) =>
	disabled &&
	css`
		opacity: 0.5;
		touch-action: none;
		pointer-events: none;
		user-select: none;
	`;

const StyledDayTable = styled(DayTable) <{ disabled: boolean }>`
	${disableElementCSS}
`;

const StyledMobileDayCarousel = styled(MobileDayCarousel) <{ disabled: boolean }>`
	${disableElementCSS}
`;

function getButtonProps<T extends Omit<EditorButtonProps, "label">>(
	buttonProps: T | undefined,
	editedProgram: ProgramContent,
) {
	return buttonProps
		? {
			...buttonProps,
			tooltip: buttonProps.disabledTooltipMessage,
			onClick: () => buttonProps.onClick?.(editedProgram),
		}
		: undefined;
}
